import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import axiosInstance, { setTokenGetter } from "./features/axiosInstance";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

import { RouterProvider } from "react-router-dom";
import { SupabaseProvider } from "./providers/supabaseProvider";
import { ThemeContextProvider } from "./providers/themeProvider";
import { Provider } from "react-redux";
import store from "./app/store";
import { router } from "./routes/mainRoutes";

import { AlertProvider } from "./context/alertContext";
import { AccessControlProvider } from "./context/AccessControlContext";
import { clearAlert } from "./features/alert/alertSlice";
import { setSessionToken, setUser } from "./features/auth/authSlice";
import {
  Alert,
  CssBaseline,
  Snackbar,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";

function App() {
  const dispatch = useDispatch();
  const { message, type, open } = useSelector((state) => state.alert);
  const sessionToken = useSelector((state) => state.auth.sessionToken);
  const idToken = useSelector((state) => state.auth.idToken);

  const { isLoading, isAuthenticated, getAccessToken, user, getIdToken } =
    useKindeAuth();
  const [tokenReady, setTokenReady] = React.useState(false);

  // Fetch and sync token from Kinde on page load/refresh if not in Redux
  useEffect(() => {
    if (!isLoading && isAuthenticated && !sessionToken) {
      const syncToken = async () => {
        try {
          const token = await getAccessToken();
          const idToken = await getIdToken();

          if (token) {
            dispatch(setSessionToken({ accessToken: token, idToken: idToken }));
            if (user) {
              dispatch(setUser(user));
            }
          }
        } catch (error) {
          // Token fetch failed
        }
      };
      syncToken();
    } else if (!isLoading && !isAuthenticated) {
      // User not authenticated
      setTokenReady(true);
    }
  }, [
    isLoading,
    isAuthenticated,
    sessionToken,
    getAccessToken,
    user,
    dispatch,
  ]);

  // Set token getter for axios interceptor to get fresh tokens
  useEffect(() => {
    setTokenGetter(getAccessToken);
  }, [getAccessToken]);

  // Mark as ready once Kinde is loaded
  useEffect(() => {
    if (!isLoading) {
      setTokenReady(true);
    }
  }, [isLoading]);

  const handleClose = (event, reason) => {
    dispatch(clearAlert());
  };

  // Show loading while Kinde is initializing or token is syncing
  if (!tokenReady || isLoading) {
    return (
      <ThemeContextProvider>
        <CssBaseline />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            backgroundColor: "#f5f5f5",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2, color: "gray" }}>
              Loading authentication...
            </Typography>
          </Box>
        </Box>
      </ThemeContextProvider>
    );
  }

  return (
    <ThemeContextProvider>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={type} variant="filled">
          {message !== undefined && message.length > 0
            ? message
            : type === "error"
              ? "An unknown error accrued please report this to the administrator."
              : ""}
        </Alert>
      </Snackbar>
      <CssBaseline />
      <AlertProvider>
        <AccessControlProvider>
          <SupabaseProvider>
            <RouterProvider router={router} />
          </SupabaseProvider>
        </AccessControlProvider>
      </AlertProvider>
    </ThemeContextProvider>
  );
}

export default App;
